import { cenario, percentuaisVariaveis } from './cenario.js';
import { precoLucroZero, precoSugerido } from './preco.js';
/**
 * Estreita para os dois estados de preço abaixo do mínimo ('prejuizo' e
 * 'abaixo'). Existe para as telas não repetirem o par de comparações — e não
 * esquecerem um dos dois, que foi o bug: quatro superfícies testavam só
 * 'abaixo' e o caso mais grave saía sem a comparação com o piso.
 */
export function pedeReajuste(r) {
    return r.estado === 'prejuizo' || r.estado === 'abaixo';
}
/* recomendacao.ts — a ÚNICA fonte dos vereditos de preço. */
/**
 * Tolerância (R$) dentro da qual preço atual e sugerido são "no ponto".
 *
 * Existe porque o preço sugerido é um número contínuo e o preço real é
 * redondo: ninguém vende por R$ 28.417,63. Sem a faixa, um projeto a
 * R$ 28.400 receberia "aumente R$ 17,63" — conselho que destrói a confiança
 * no sistema inteiro por parecer um robô sem noção.
 */
export const TOLERANCIA = 50;
/**
 * recomendar(p): o veredito, em SETE estados mutuamente exclusivos.
 *
 * REGRA INEGOCIÁVEL: se a margem atual já é maior ou igual à meta, o motor
 * NUNCA sugere reduzir preço. O preço sugerido é um MÍNIMO para atingir a
 * meta, não um alvo a perseguir para baixo. Um sistema que dissesse "você pode
 * cobrar R$ 8 mil a menos" estaria destruindo a margem do cliente com cara de
 * conselho técnico.
 *
 * Essa regra não é comentário: no tipo `RecomendacaoAcima` os campos
 * `reajuste` e `reducaoKit` NÃO EXISTEM. Nenhuma tela consegue lê-los ali nem
 * por engano — o compilador recusa antes.
 */
export function recomendar(p) {
    // O veredito é sobre um PROJETO, então o preço tem de ser o do projeto.
    //
    // `montarParametros` preenche `ticket` com a média dos projetos da
    // sazonalidade quando o projeto não tem valor de venda (parametros.ts:159).
    // Esse fallback é legítimo e continua existindo: Indicadores e Sazonalidade
    // o usam com o rótulo certo, e cortá-lo apagaria verdade em duas telas para
    // consertar mentira em uma. O que não é legítimo é emitir VEREDITO sobre
    // ele: o numerador viria de um objeto (a média da empresa, de projetos de
    // potências desconhecidas) e o denominador de outro (o kwp DESTE projeto,
    // que puxa cdt e kit por faixa).
    //
    // A recusa mora aqui, no core, e não na tela, porque o servidor é a
    // autoridade: `GET /api/calculo/analise` recalcula do banco com o mesmo
    // motor. Se a guarda fosse só de tela, a tela diria "sem dados" enquanto a
    // API devolvia veredito completo para o mesmo projeto no mesmo instante, que
    // é a divergência painel contra PDF que o ARQUITETURA.md registra como o
    // erro caro da versão anterior.
    if (p.ticketOrigem !== 'projeto' || !(p.ticket > 0)) {
        return { estado: 'sem-dados', falta: 'ticket' };
    }
    const ps = precoSugerido(p);
    if (!ps.valido) {
        // Três recusas, três ações diferentes do usuário:
        //   'sem-volume'          → dado faltando. Ele resolve preenchendo.
        //   'percentual-invalido' → dado ERRADO já gravado. Ele resolve corrigindo,
        //                           e a lista diz qual campo.
        //   'meta-inviavel'       → conflito de configuração. Ele resolve decidindo.
        switch (ps.motivo) {
            case 'sem-volume':
                return { estado: 'sem-dados', falta: 'volume' };
            case 'percentual-invalido':
                return { estado: 'percentual-invalido', invalidos: ps.invalidos };
            case 'meta-inviavel':
                return { estado: 'meta-inviavel' };
        }
    }
    const atual = cenario(p.ticket, p);
    const sugerido = cenario(ps.valor, p);
    const diff = ps.valor - p.ticket;
    // O piso de LUCRO ZERO é a mesma inversão com a meta zerada, então ele é
    // sempre <= precoSugerido e nunca recusa quando precoSugerido aceitou:
    // zerar a meta só AUMENTA o denominador. O fallback existe para o tipo.
    const plz = precoLucroZero(p);
    const pisoLucroZero = plz.valido ? plz.valor : ps.valor;
    // A opção B (cortar o kit) é a mesma conta nos dois estados de preço baixo.
    // Fica aqui, calculada uma vez, em vez de duplicada nos dois ramos.
    //
    // A conta tem uma sutileza: baixar o kit AUMENTA a base de serviço
    // (srv = preço − kit), então parte do desconto volta como mais
    // comissão/tributo de base serviço — cada R$ 1 cortado vira só (1 − pctServ)
    // de margem. Mas o clamp srvIncid = max(0, srv) de `cenario` torna esse
    // ganho PIECEWISE: enquanto o kit está ACIMA do ticket a base de serviço
    // segue zerada e cada R$ 1 cortado vira R$ 1 inteiro de margem; só depois
    // que o kit cruza o ticket é que o fator (1 − pctServ) entra. Usar um único
    // fator inflava o corte no estado `prejuizo` (kit > ticket): pedia R$ 10.555
    // de desconto onde R$ 10.000 fechavam a meta exata.
    function opcaoB() {
        const pcts = percentuaisVariaveis(p);
        const gapMC = (p.lucroAlvo / 100) * p.ticket + (sugerido.fixUnit ?? 0) - atual.mcUnit;
        /** Trecho em que o corte rende R$ 1 por R$ 1: do kit atual até o ticket. */
        const trechoInteiro = Math.max(0, p.kit - p.ticket);
        const restante = gapMC - trechoInteiro;
        const bruto = restante <= 0
            ? gapMC
            : 1 - pcts.serv > 1e-9
                ? trechoInteiro + restante / (1 - pcts.serv)
                : null;
        // Só sugerimos cortar o kit se o corte couber DENTRO do kit. Pedir para
        // tirar R$ 20 mil de um kit de R$ 12 mil não é conselho, é ruído.
        return bruto !== null && bruto > 0 && bruto < p.kit ? bruto : null;
    }
    // PREJUÍZO antes de ABAIXO, e com a tolerância no predicado.
    //
    // Sem a tolerância aqui o produto escreveria "cada venda consome do caixa"
    // por R$ 1 de prejuízo, que é o robô sem noção que a docstring da constante
    // existe para evitar. E a conversão entre distância em PREÇO e distância em
    // LUCRO não é 1 para 1: vale
    //   lucroUnit = (1 − pctTotal − pctServ) × (ticket − precoLucroZero)
    // e o fator é menor que 1, então testar o SINAL de lucroUnit dispararia num
    // ponto diferente de testar a distância em preço. O predicado é a distância
    // em preço, que é a grandeza que a tolerância mede.
    //
    // Com lucroAlvo = 0 os dois pisos coincidem e 'abaixo' fica vazio por
    // construção, o que é a leitura certa: sem meta, ficar abaixo do piso da
    // meta É ficar abaixo do lucro zero.
    if (p.ticket < pisoLucroZero - TOLERANCIA) {
        return {
            estado: 'prejuizo',
            precoSugerido: ps.valor,
            precoLucroZero: pisoLucroZero,
            tolerancia: TOLERANCIA,
            atual,
            sugerido,
            reajuste: diff,
            reajustePct: diff / p.ticket,
            reducaoKit: opcaoB(),
        };
    }
    if (diff > TOLERANCIA) {
        // Abaixo do mínimo. Duas saídas, e a segunda é a que o integrador costuma
        // conseguir de fato:
        //   Opção A: subir o preço em `reajuste`.
        //   Opção B: manter o preço e negociar `reducaoKit` a menos com a
        //            distribuidora (a conta, e sua sutileza, vivem em `opcaoB`).
        return {
            estado: 'abaixo',
            precoSugerido: ps.valor,
            tolerancia: TOLERANCIA,
            atual,
            sugerido,
            reajuste: diff,
            reajustePct: diff / p.ticket,
            reducaoKit: opcaoB(),
        };
    }
    if (diff >= -TOLERANCIA) {
        return { estado: 'no-ponto', precoSugerido: ps.valor, tolerancia: TOLERANCIA, atual, sugerido };
    }
    // Preço atual ACIMA do mínimo: margem superior à meta. Celebrar, nunca
    // sugerir cortar. A folga informa poder de negociação — é quanto o
    // integrador pode ceder num desconto sem furar a própria meta.
    return {
        estado: 'acima',
        precoSugerido: ps.valor,
        tolerancia: TOLERANCIA,
        atual,
        sugerido,
        folga: p.ticket - ps.valor,
        folgaPct: (p.ticket - ps.valor) / p.ticket,
    };
}
//# sourceMappingURL=recomendacao.js.map